import React, { useRef, useState, useMemo } from 'react';
import { Layers, Tag, ArrowRightLeft, Trash2, Download, Upload, ChevronRight, Search, X, Send } from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';
import { safeAsync, btnPrimary, btnSm, cardWrap, neuShadow, neuShadowSm, inp } from './crmShared';
import { matchesNameQuery } from '../../lib/nameSearch';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function getGroupKey(lastName) {
  if (!lastName || lastName.trim() === '') return '#';
  const firstChar = lastName.charAt(0).toUpperCase();
  return /^[A-Z]$/.test(firstChar) ? firstChar : '#';
}

export default function BulkOpsTab({ contacts, selectedContacts, setSelectedContacts, tags, loadExtended, loadData, setShowBulkSend }) {
  const contactListRef = useRef(null);
  const { onKeyDown: handleContactsKeyDown } = useArrowNav(contactListRef, '[data-contact-item]', { horizontal: false, vertical: true });

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedLetters, setExpandedLetters] = useState(new Set());

  // Group contacts by last name's first letter (A-Z, #)
  const groupedContacts = useMemo(() => {
    const groups = {};
    contacts.forEach((contact) => {
      const key = getGroupKey(contact.last_name);
      if (!groups[key]) groups[key] = [];
      groups[key].push(contact);
    });

    // Sort contacts within each group by last name
    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        const lastNameA = (a.last_name || '').toLowerCase();
        const lastNameB = (b.last_name || '').toLowerCase();
        if (lastNameA !== lastNameB) return lastNameA.localeCompare(lastNameB);
        return (a.first_name || '').localeCompare(b.first_name || '');
      });
    });

    return groups;
  }, [contacts]);

  // Filter contacts based on search query
  const filteredGroupedContacts = useMemo(() => {
    if (!searchQuery.trim()) return groupedContacts;

    const query = searchQuery.toLowerCase();
    const filtered = {};
    Object.entries(groupedContacts).forEach(([key, items]) => {
      const filtered_items = items.filter((c) =>
        matchesNameQuery(
          [c.first_name, c.last_name, c.email, c.contact_type, c.pipeline_stage], query));
      if (filtered_items.length > 0) {
        filtered[key] = filtered_items;
      }
    });
    return filtered;
  }, [groupedContacts, searchQuery]);

  // Auto-expand all groups when search is active
  const isSearchActive = searchQuery.trim() !== '';
  const displayExpandedLetters = isSearchActive ? new Set(Object.keys(filteredGroupedContacts)) : expandedLetters;

  const toggleGroup = (letter) => {
    const newExpanded = new Set(displayExpandedLetters);
    if (newExpanded.has(letter)) {
      newExpanded.delete(letter);
    } else {
      newExpanded.add(letter);
    }
    setExpandedLetters(newExpanded);
  };

  const expandAll = () => {
    setExpandedLetters(new Set(Object.keys(filteredGroupedContacts)));
  };

  const collapseAll = () => {
    setExpandedLetters(new Set());
  };

  const handleQuickJump = (letter) => {
    const element = document.getElementById(`group-${letter}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Get all visible contact IDs (from filtered groups)
  const visibleContactIds = useMemo(() => {
    return Object.values(filteredGroupedContacts)
      .flat()
      .map((c) => c.id);
  }, [filteredGroupedContacts]);

  const allContactsSelected = visibleContactIds.length > 0 && visibleContactIds.every((id) => selectedContacts.includes(id));
  const someContactsSelected = visibleContactIds.some((id) => selectedContacts.includes(id)) && !allContactsSelected;

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedContacts(Array.from(new Set([...selectedContacts, ...visibleContactIds])));
    } else {
      setSelectedContacts(selectedContacts.filter((id) => !visibleContactIds.includes(id)));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Layers size={18} color="#F26522" /> Bulk Operations
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setShowBulkSend?.(true)} style={btnPrimary} title="Filter recipients + send SMS or email blast">
            <Send size={13} /> Bulk Send Message
          </button>
          <div style={{ fontSize: 11, color: '#6C757D' }}>
            {selectedContacts.length > 0 ? <span style={{ fontWeight: 700, color: '#2E8B98' }}>{selectedContacts.length} contacts selected</span> : 'Select contacts below'}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button disabled={selectedContacts.length === 0} onClick={safeAsync(async () => {
          const tagName = prompt('Tag name to add:');
          if (!tagName) return;
          const tag = tags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
          if (!tag) { alert('Tag not found'); return; }
          const res = await window.zignagenixAPI.bulkTag({ contactIds: selectedContacts, tagId: tag.id, action: 'add' });
          alert(`Added tag to ${res.changed} contacts`);
        })} style={btnPrimary}><Tag size={13} /> Bulk Add Tag</button>

        <button disabled={selectedContacts.length === 0} onClick={safeAsync(async () => {
          const stage = prompt('Move to stage:');
          if (!stage) return;
          const res = await window.zignagenixAPI.bulkMoveStage({ contactIds: selectedContacts, stage });
          alert(`Moved ${res.moved} contacts to ${stage}`);
          loadData(); loadExtended();
        })} style={btnPrimary}><ArrowRightLeft size={13} /> Bulk Move Stage</button>

        <button disabled={selectedContacts.length === 0} onClick={safeAsync(async () => {
          if (!window.confirm(`Soft-delete ${selectedContacts.length} contacts?`)) return;
          const res = await window.zignagenixAPI.bulkDelete({ contactIds: selectedContacts });
          alert(`Deleted ${res.deleted} contacts`); setSelectedContacts([]);
          loadData(); loadExtended();
        })} style={{ ...btnSm, color: '#DC3545' }}><Trash2 size={13} /> Bulk Delete</button>

        <button onClick={safeAsync(async () => {
          const data = await window.zignagenixAPI.bulkExport({});
          const csv = [Object.keys(data[0] || {}).join(','), ...data.map(r => Object.values(r).map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(','))].join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = 'crm_contacts_export.csv'; a.click();
          URL.revokeObjectURL(url);
        })} style={btnSm}><Download size={13} /> Export All CSV</button>

        <button onClick={safeAsync(async () => {
          const csvText = prompt('Paste CSV data (first_name,last_name,email,phone,contact_type):');
          if (!csvText) return;
          const lines = csvText.split('\n').filter(l => l.trim());
          const contacts = lines.map(l => { const [first_name, last_name, email, phone, contact_type] = l.split(',').map(s => s.trim().replace(/^"|"$/g, '')); return { first_name, last_name, email, phone, contact_type }; });
          const res = await window.zignagenixAPI.bulkImport({ contacts });
          alert(`Imported: ${res.imported}, Skipped: ${res.skipped}`);
          loadData(); loadExtended();
        })} style={btnSm}><Upload size={13} /> Import CSV</button>
      </div>

      {/* Contact selector with accordion */}
      <div ref={contactListRef} onKeyDown={handleContactsKeyDown} style={cardWrap}>
        {/* Search bar */}
        <div style={{ padding: '12px 16px', background: '#f8f9fa', borderBottom: '1px solid #dce2e8', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Search size={16} color="#6C757D" />
          <input
            type="text"
            placeholder="Search by name, email, type, or stage..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...inp, flex: 1, fontSize: 12, padding: '6px 10px' }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <X size={14} color="#6C757D" />
            </button>
          )}
        </div>

        {/* Expand/Collapse all buttons */}
        <div style={{ padding: '10px 16px', background: '#e0e5ec', borderBottom: '1px solid #dce2e8', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={expandAll} style={{ ...btnSm, fontSize: 11, padding: '4px 10px' }}>Expand All</button>
          <button onClick={collapseAll} style={{ ...btnSm, fontSize: 11, padding: '4px 10px' }}>Collapse All</button>
        </div>

        {/* Alphabet quick-jump bar */}
        <div style={{ padding: '10px 16px', background: '#f8f9fa', borderBottom: '1px solid #dce2e8', display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          {ALPHABET.map((letter) => {
            const hasContacts = letter in filteredGroupedContacts;
            return (
              <button
                key={letter}
                onClick={() => handleQuickJump(letter)}
                disabled={!hasContacts}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '4px 8px',
                  border: 'none',
                  background: hasContacts ? '#dce2e8' : 'transparent',
                  color: hasContacts ? '#212529' : '#ADB5BD',
                  cursor: hasContacts ? 'pointer' : 'default',
                  borderRadius: 3,
                  transition: 'all 0.2s',
                }}
              >
                {letter}
              </button>
            );
          })}
          <button
            onClick={() => handleQuickJump('#')}
            disabled={!('#' in filteredGroupedContacts)}
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '4px 8px',
              border: 'none',
              background: '#' in filteredGroupedContacts ? '#dce2e8' : 'transparent',
              color: '#' in filteredGroupedContacts ? '#212529' : '#ADB5BD',
              cursor: '#' in filteredGroupedContacts ? 'pointer' : 'default',
              borderRadius: 3,
              transition: 'all 0.2s',
            }}
          >
            #
          </button>
        </div>

        {/* Select all checkbox */}
        <div style={{ padding: '8px 16px', background: '#e0e5ec', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #dce2e8' }}>
          <input
            type="checkbox"
            checked={allContactsSelected}
            ref={(el) => {
              if (el) el.indeterminate = someContactsSelected;
            }}
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
          <span style={{ fontSize: 11, fontWeight: 600, color: '#6C757D' }}>
            Select All ({visibleContactIds.length})
          </span>
        </div>

        {/* Grouped accordion sections */}
        {Object.keys(filteredGroupedContacts)
          .sort((a, b) => {
            if (a === '#') return 1;
            if (b === '#') return -1;
            return a.localeCompare(b);
          })
          .map((letter) => {
            const groupContacts = filteredGroupedContacts[letter];
            const isExpanded = displayExpandedLetters.has(letter);
            const count = groupContacts.length;

            return (
              <div key={letter}>
                {/* Group header */}
                <div
                  id={`group-${letter}`}
                  onClick={() => !isSearchActive && toggleGroup(letter)}
                  style={{
                    padding: '10px 16px',
                    background: '#f0f3f7',
                    borderBottom: '1px solid #dce2e8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: !isSearchActive ? 'pointer' : 'default',
                    userSelect: 'none',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSearchActive) e.currentTarget.style.background = '#e0e5ec';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f0f3f7';
                  }}
                >
                  <ChevronRight
                    size={16}
                    color="#6C757D"
                    style={{
                      transition: 'transform 0.2s',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#212529', minWidth: 20 }}>{letter}</span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      background: '#2E8B98',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: 3,
                    }}
                  >
                    {count}
                  </span>
                </div>

                {/* Expandable contacts list */}
                <div
                  style={{
                    maxHeight: isExpanded ? `${groupContacts.length * 36}px` : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease-in-out',
                  }}
                >
                  {groupContacts.map((c, i) => (
                    <div
                      key={c.id}
                      data-contact-item
                      tabIndex={0}
                      style={{
                        padding: '6px 16px',
                        paddingLeft: '40px',
                        borderBottom: '1px solid #dce2e8',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        background: i % 2 === 1 ? '#F8F9FA' : '#FFFFFF',
                        outline: 'none',
                        transition: 'background 0.2s',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.backgroundColor = '#dce2e8';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.backgroundColor = i % 2 === 1 ? '#F8F9FA' : '#FFFFFF';
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedContacts.includes(c.id)}
                        onChange={(e) =>
                          setSelectedContacts(
                            e.target.checked
                              ? [...selectedContacts, c.id]
                              : selectedContacts.filter((id) => id !== c.id)
                          )
                        }
                      />
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#212529', width: 160 }}>
                        {c.first_name} {c.last_name}
                      </span>
                      <span style={{ fontSize: 10, color: '#6C757D', width: 100 }}>{c.contact_type}</span>
                      <span style={{ fontSize: 10, color: '#ADB5BD' }}>{c.email || '—'}</span>
                      <span style={{ fontSize: 10, color: '#ADB5BD', marginLeft: 'auto' }}>
                        {c.pipeline_stage?.replace(/_/g, ' ') || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

        {/* Empty state */}
        {Object.keys(filteredGroupedContacts).length === 0 && (
          <div style={{ padding: '20px 16px', textAlign: 'center', color: '#6C757D', fontSize: 12 }}>
            {searchQuery ? 'No contacts match your search' : 'No contacts available'}
          </div>
        )}
      </div>
    </div>
  );
}
